import React, { useLayoutEffect, useRef } from 'react';
import {
  MapPin,
  MessageCircle,
  Heart,
  ClipboardPlus,
  ChartNoAxesCombined,
  TrendingUp,
  Settings,
  UserRound,
} from 'lucide-react';
import { useTranslations } from '../translations';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import type { AccountTab } from '../utils/accountTabReveal';
import {
  consumeAccountTabReveal,
  navigateToAccountTab,
} from '../utils/accountTabReveal';
import StorefrontIcon from './StorefrontIcon';
import './UserSectionSidebar.css';

type UserSectionItem = AccountTab;

interface UserSectionSidebarProps {
  activeItem: UserSectionItem;
  variant?: 'sidebar' | 'tabs';
  onProfileClick?: () => void;
  onFavoritesClick?: () => void;
  onLocationsClick?: () => void;
  onMessagesClick?: () => void;
  onAnnouncementsClick?: () => void;
  onSettingsClick?: () => void;
}

interface NavigationItem {
  id: UserSectionItem;
  label: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  onClick?: () => void;
}

let accountTabsScrollLeft = 0;

const UserSectionSidebar: React.FC<UserSectionSidebarProps> = ({
  activeItem,
  variant = 'sidebar',
  onProfileClick,
  onFavoritesClick,
  onLocationsClick,
  onMessagesClick,
  onAnnouncementsClick,
  onSettingsClick,
}) => {
  const t = useTranslations();
  const { hasUnreadMessages } = useUnreadMessages();
  const itemsRef = useRef<HTMLDivElement>(null);

  const navigationItems: NavigationItem[] = [
    {
      id: 'profile',
      label: t.accountDropdown.profile,
      icon: UserRound,
      onClick: onProfileClick,
    },
    {
      id: 'announcements',
      label: t.accountDropdown.announcements,
      icon: StorefrontIcon,
      onClick: onAnnouncementsClick,
    },
    {
      id: 'favorites',
      label: t.accountDropdown.favorites,
      icon: Heart,
      onClick: onFavoritesClick ?? (() => navigateToAccountTab('favorites', false)),
    },
    {
      id: 'messages',
      label: t.accountDropdown.messages,
      icon: MessageCircle,
      onClick: onMessagesClick,
    },
    {
      id: 'orders',
      label: t.accountDropdown.orders,
      icon: ClipboardPlus,
      onClick: () => navigateToAccountTab('orders', false),
    },
    {
      id: 'orderStatistics',
      label: t.accountDropdown.orderStatistics,
      icon: TrendingUp,
      onClick: () => navigateToAccountTab('orderStatistics', false),
    },
    {
      id: 'orderReport',
      label: t.accountDropdown.orderReport,
      icon: ChartNoAxesCombined,
      onClick: () => navigateToAccountTab('orderReport', false),
    },
    {
      id: 'locations',
      label: t.accountDropdown.locations,
      icon: MapPin,
      onClick: onLocationsClick,
    },
    {
      id: 'settings',
      label: t.accountDropdown.settings,
      icon: Settings,
      onClick: onSettingsClick,
    },
  ];

  useLayoutEffect(() => {
    const items = itemsRef.current;
    if (!items || items.clientWidth === 0) {
      return;
    }

    const shouldRevealActiveTab = consumeAccountTabReveal(activeItem);

    if (items.scrollWidth <= items.clientWidth) {
      return;
    }

    items.scrollLeft = accountTabsScrollLeft;

    if (shouldRevealActiveTab) {
      const activeTab = items.querySelector<HTMLElement>(
        `[data-account-tab="${activeItem}"]`,
      );

      if (activeTab) {
        const centeredScrollLeft = activeTab.offsetLeft
          - ((items.clientWidth - activeTab.offsetWidth) / 2);
        const maximumScrollLeft = items.scrollWidth - items.clientWidth;
        items.scrollLeft = Math.max(0, Math.min(centeredScrollLeft, maximumScrollLeft));
        accountTabsScrollLeft = items.scrollLeft;
      }
    }
  }, [activeItem]);

  const handleItemsScroll = () => {
    const items = itemsRef.current;
    if (items && items.scrollWidth > items.clientWidth) {
      accountTabsScrollLeft = items.scrollLeft;
    }
  };

  return (
    <nav
      className={`user-section-sidebar ${variant === 'tabs' ? 'user-section-sidebar-tabs' : ''}`}
      aria-label={t.header.myAccount}
    >
      <h2 className="user-section-sidebar-title">{t.header.myAccount}</h2>
      <div
        className="user-section-sidebar-items"
        ref={itemsRef}
        onScroll={handleItemsScroll}
      >
        {navigationItems.map(({ id, label, icon: Icon, onClick }) => {
          const isActive = activeItem === id;

          return (
            <button
              key={id}
              type="button"
              className={`user-section-sidebar-item ${isActive ? 'active' : ''}`}
              data-account-tab={id}
              onClick={onClick}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="user-section-sidebar-icon" aria-hidden />
              <span className="user-section-sidebar-label">{label}</span>
              {id === 'messages' && hasUnreadMessages && (
                <span className="user-section-sidebar-notification-dot" aria-label="Mesaje necitite" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default UserSectionSidebar;
